<template>
	<div class="honda-flat-rate-search-results-view">
		<InfiniteScroller
			:loading="loading"
			@scrollBoundary="getNextPage"
			:hasMoreResults="hasMoreResults"
			:wheelElement="tableElement"
			:scrollElement="tableContainer"
		>
			<template v-slot:scrollDiv>
				<div>
					<Table
						:isTotalRow="false"
						:recordset="recordset"
						:columns="tableColumns"
						@sort="onSortTable"
						@mounted="tableMounted"
						ref="table"
						:responsive="false"
						:minHeight="hasResults ? 0 : 250"
						:maxHeightOverride="348"
					>
						<template v-slot:laborOpNumber="slotProps">
							<div
								class="honda-flat-rate-search-results-view__labor-code"
							>
								{{ slotProps.record.laborOpNumber }}
							</div>
						</template>
						<template v-slot:description="slotProps">
							<div
								class="honda-flat-rate-search-results-view__description"
							>
								<TruncatedValue
									:value="slotProps.record.description"
									:hasTooltip="false"
								/>
							</div>
						</template>
						<template v-slot:flatRateTime="slotProps">
							<div
								class="honda-flat-rate-search-results-view__flat-rate-time"
							>
								{{ slotProps.record.flatRateTime }}
							</div>
						</template>
						<template v-slot:defectCodes="slotProps">
							<Hyperlink
								class="honda-flat-rate-search-results-view__defect-codes"
								:ref="`viewdefectcodes${slotProps.record.id}`"
								labelText="View"
								@click="onViewDefectCodes(slotProps.record.id)"
							/>
							<Popover
								class="honda-flat-rate-search-results-view__popover"
								:visible="
									defectCodesVisibleRecordId ===
										slotProps.record.id
								"
								@close="onCloseDefectCodes(slotProps.record.id)"
								:trackedRefName="
									$refs[
										`viewdefectcodes${slotProps.record.id}`
									]
								"
								positionStrategy="fixed"
								container="root"
								:zIndex="2500"
								:offset="[0, 16]"
							>
								<template v-slot:popoverTitle>
									<div
										class="honda-flat-rate-search-results-view__popover-title"
									>
										Import Defect Code
									</div>
								</template>
								<template v-slot:popoverBody>
									<ul
										class="honda-flat-rate-search-results-view__popover-items"
									>
										<li
											class="honda-flat-rate-search-results-view__popover-item"
											v-for="defectCodes in slotProps
												.record.defectCodes"
											:key="defectCodes.id"
										>
											<ButtonIcon
												class="honda-flat-rate-search-results-view__add-btn"
												iconClassName="icon-add_filled"
												@click="
													onAddDefectCode(
														defectCodes.defectCode
													)
												"
											/>
											<span
												class="honda-flat-rate-search-results-view__defect-code"
												>{{ defectCodes.defectCode }}
											</span>
										</li>
									</ul>
								</template>
							</Popover>
						</template>
						<template v-slot:addLaborCode="slotProps">
							<span
								class="honda-flat-rate-search-results-view__labor-code-btn"
								@mouseenter="
									onMouseEnterAddLaborCode(
										slotProps.record.id
									)
								"
								@mouseleave="onMouseLeaveAddLaborCode"
							>
								<ButtonIcon
									class="honda-flat-rate-search-results-view__labor-code-icon"
									:disabled="
										addLaborCodeDisabled(
											slotProps.record.id
										)
									"
									iconClassName="icon-add_filled"
									@click="onAddLaborCode(slotProps.record)"
									:ref="
										`addLoborCodeBtn${slotProps.record.id}`
									"
								/>
								<Tooltip
									data-testid="tooltip-data"
									:visible="
										isAddLaborCodeToolTipVisible(
											slotProps.record.id
										)
									"
									:trackedRefName="
										$refs[
											`addLoborCodeBtn${slotProps.record.id}`
										]
									"
									positionStrategy="fixed"
									:offset="[0, 16]"
								>
									This labor code has already been selected
									for import.
								</Tooltip>
							</span>
						</template>
						<template v-slot:noResults>
							<NoResultsMessage
								:hasSearchFilter="hasSearchFilter"
								:visible="!hasResults"
								primaryTextCondensed="No Labor Operation Codes to display."
								secondaryTextCondensed="Search a Keyword or Labor Code to return results."
							/>
						</template>
					</Table>
				</div>
			</template>
		</InfiniteScroller>
	</div>
</template>

<script>
import Hyperlink from '@/components/typography/hyperlink/hyperlink.vue';
import Table from '@/components/shells/table/table.vue';
import { mapGetters } from 'vuex';
import NoResultsMessage from '@/components/shells/no-results-message/no-results-message.vue';
import columns from './honda-flat-rate-columns';
import { ASC } from '@/components/shells/table/sort-directions';
import Popover from '@/components/shells/popover/popover.vue';
import InfiniteScroller from '@/components/shells/infinite-scroller/infinite-scroller.vue';
import Tooltip from '@/components/shells/tooltip/tooltip.vue';
import TruncatedValue from '@/components/shells/truncated-value/truncated-value.vue';
import ButtonIcon from '@/components/forms/button/button-icon.vue';

export default {
	name: 'HondFlatRateSearchResults',
	components: {
		NoResultsMessage,
		Hyperlink,
		Table,
		Popover,
		InfiniteScroller,
		Tooltip,
		TruncatedValue,
		ButtonIcon
	},
	data() {
		return {
			sortBy: 'laborOpNumber',
			sortDirection: ASC,
			currentPage: 1,
			infiniteScrollElement: undefined,
			tableElement: undefined,
			defectCodesVisibleRecordId: 0,
			addLaborCodeTooltipVisibleId: 0
		};
	},
	props: {
		recordset: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		},
		hasSearchFilter: {
			type: Boolean,
			default: false
		},
		hasMoreResults: {
			type: Boolean,
			default: false
		}
	},
	async created() {},
	computed: {
		...mapGetters('hondaFlatRate', ['selectedLaborCodes']),
		tableContainer() {
			return this.infiniteScrollElement;
		},
		tableColumns() {
			const tempColumns = [...columns];
			let columnIndex;

			if (this.sortBy) {
				columnIndex = tempColumns.findIndex(
					c => c.columnName === this.sortBy
				);

				tempColumns[columnIndex].sortDirection = this.sortDirection;
			}

			return tempColumns;
		},
		hasResults() {
			return this.recordset?.length > 0;
		}
	},
	methods: {
		onSortTable(objSort) {
			this.$emit('sort', {
				columnName: objSort.columnName,
				sortDirection: objSort.sortDirection
			});
		},
		onViewDefectCodes(id) {
			this.defectCodesVisibleRecordId = id;
		},
		onCloseDefectCodes() {
			this.defectCodesVisibleRecordId = 0;
		},
		onAddDefectCode(defectCode) {
			this.$emit('addDefectCode', defectCode);
			this.onCloseDefectCodes();
		},
		onAddLaborCode(laborRateRecord) {
			this.$emit('addLaborCode', laborRateRecord);
		},
		addLaborCodeDisabled(id) {
			return this.selectedLaborCodes.some(code => code.id === id);
		},
		getNextPage() {
			this.currentPage++;
			this.$emit('nextPage', this.currentPage);
		},
		tableMounted(tableReferences) {
			if (tableReferences) {
				this.infiniteScrollElement = tableReferences.tableContainer;
				this.tableElement = tableReferences.table;
			}
		},
		onMouseEnterAddLaborCode(id) {
			this.addLaborCodeTooltipVisibleId = id;
		},
		onMouseLeaveAddLaborCode() {
			this.addLaborCodeTooltipVisibleId = 0;
		},
		isAddLaborCodeToolTipVisible(id) {
			if (this.addLaborCodeDisabled(id)) {
				return this.addLaborCodeTooltipVisibleId === id;
			}
			return false;
		}
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar(
	'borderless',
	'.honda-flat-rate-search-results-view__popover-items'
);
.honda-flat-rate-search-results-view {
	&__popover-items {
		display: flex;
		flex-direction: column;
		gap: $gutter-md;
		width: 100%;
		overflow: auto;
		max-height: 300px;
		border: 1px solid var(--neutral-2);
	}

	&__add-btn {
		font-size: $txt-font-size;
	}

	&__popover-item {
		display: flex;
		align-items: center;
	}

	&__description {
		overflow-wrap: break-word;
		word-break: break-all;
		max-width: 250px;

		@include mq($from: lg) {
			max-width: 480px;
		}
	}

	&__defect-code {
		font-size: $txt-font-size;
	}

	&:deep(.table__td) {
		vertical-align: middle;
	}

	&:deep(.table__th--labor-code),
	&:deep(.table__td--labor-code) {
		width: 109px;
	}

	&:deep(.table__th--description),
	&:deep(.table__td--description) {
		width: auto;
	}

	&:deep(.table__th--flat-rate-time),
	&:deep(.table__td--flat-rate-time) {
		width: 63px;
	}

	&:deep(.table__th--defect-codes),
	&:deep(.table__td--defect-codes) {
		width: 103px;
		text-align: center;
	}

	&:deep(.table__th--add-labor-code),
	&:deep(.table__td--add-labor-code) {
		width: 39px;
		padding: $gutter-md 0;
	}

	&:deep(.table__td--add-labor-code) {
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	&__labor-code-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;

		&:deep(.btn-small) {
			min-width: 0;
		}
	}

	&__labor-code-icon {
		font-size: $table-icon-font-size;
	}
}
.tooltip {
	pointer-events: none;
}
</style>
